import { Button, DataTable, type DataTableColumn, IconButton, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  type ApiKeyResponse,
  errorMessage,
  listApiKeysOptions,
  listApiKeysQueryKey,
  revokeApiKeyMutation,
} from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageSection } from "@/components/section/SectionBlocks";
import { SectionError } from "@/components/section/SectionStates";
import { Text } from "@/components/Text";
import { formatDate } from "@/i18n/format";
import { isApiKeyScope } from "./apiKeyScopes";

function useColumns(): DataTableColumn<ApiKeyResponse>[] {
  const { t } = useTranslation("settings");
  const muted = (value: string) => (
    <Text as="span" variant="body-sm" tone="tertiary">
      {value}
    </Text>
  );
  return [
    {
      key: "name",
      header: t("security.keys.columns.name"),
      mobile: "primary",
      render: (key) => (
        <div className="flex min-w-0 flex-col text-left">
          <Text as="span" variant="body-sm" className="truncate">
            {key.name}
          </Text>
          <code className="[font:var(--type-code)] text-content-tertiary">{`${key.key_prefix}…`}</code>
        </div>
      ),
    },
    {
      key: "scopes",
      header: t("security.keys.columns.scopes"),
      render: (key) =>
        muted(key.scopes.map((s) => (isApiKeyScope(s) ? t(`security.scopes.${s}.label`) : s)).join(", ")),
    },
    {
      key: "created",
      header: t("security.keys.columns.created"),
      render: (key) => muted(formatDate(key.created_at)),
    },
    {
      key: "lastUsed",
      header: t("security.keys.columns.lastUsed"),
      render: (key) => muted(key.last_used_at ? formatDate(key.last_used_at) : t("security.keys.never")),
    },
    {
      key: "expires",
      header: t("security.keys.columns.expires"),
      render: (key) => muted(key.expires_at ? formatDate(key.expires_at) : t("security.keys.noExpiry")),
    },
  ];
}

export function ApiKeysSection() {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const keys = useQuery(listApiKeysOptions());
  const [target, setTarget] = useState<ApiKeyResponse | null>(null);
  const columns = useColumns();
  const revoke = useMutation({
    ...revokeApiKeyMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: listApiKeysQueryKey() });
      toast.success(t("security.keys.revoked"));
      setTarget(null);
    },
    onError: (error) => toast.error(errorMessage(error, t("security.keys.revokeFailed"))),
  });
  const create = () => navigate("/settings/security/api-keys/new");

  return (
    <PageSection
      title={t("security.keys.title")}
      description={t("security.keys.description")}
      actions={
        <Button variant="primary" size="sm" leadingIcon={<Plus size={16} />} onClick={create}>
          {t("security.keys.new")}
        </Button>
      }
    >
      {keys.isError ? (
        <SectionError onRetry={() => void keys.refetch()} error={keys.error} />
      ) : (
        <DataTable
          aria-label={t("security.keys.tableLabel")}
          columns={columns}
          rows={keys.data ?? []}
          rowKey={(key) => key.id}
          loading={keys.isPending}
          actionsLabel={t("security.keys.columns.actions")}
          renderActions={(key) => (
            <IconButton
              icon={<Trash2 />}
              aria-label={t("security.keys.revokeLabel", { name: key.name })}
              variant="ghost"
              size="sm"
              onClick={() => setTarget(key)}
            />
          )}
          empty={
            <EmptyState
              icon={<KeyRound size={32} />}
              title={t("security.keys.empty")}
              body={t("security.keys.emptyBody")}
            />
          }
        />
      )}
      <ConfirmDialog
        open={target !== null}
        title={t("security.keys.revokeTitle", { name: target?.name ?? "" })}
        body={t("security.keys.revokeBody")}
        confirmLabel={t("security.keys.revoke")}
        loading={revoke.isPending}
        onConfirm={() => target && revoke.mutate({ path: { key_id: target.id } })}
        onClose={() => setTarget(null)}
      />
    </PageSection>
  );
}
